import React, { useState, useEffect } from "react";
import axios from "axios";
import districts from "../../data/district.json";
import upazilas from "../../data/upazila.json";
import donorPlaceholder from "../../assets/image/noimage.gif";
import api from "../../utils/axios.config";
import toast from "react-hot-toast";
import { FaTrash } from "react-icons/fa";
import { baseUrl } from "../../utils/url";

const bloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const ManageBloodDonor = () => {
  const [donors, setDonors] = useState([]);
  const [search, setSearch] = useState("");
  const [district, setDistrict] = useState("");
  const [upazila, setUpazila] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [bloodGroup, setBloodGroup] = useState("");

  useEffect(() => {
    fetchDonors();
  }, [search, district, upazila, page, bloodGroup]);

  const fetchDonors = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("blood-donor", {
        params: {
          search,
          district: getDistrictById(district),
          upazila: getUpazilaById(upazila),
          bloodGroup,
          page,
          limit: 10,
        },
      });
      setDonors(response.data.data);
      setTotalPages(Math.ceil(response.data.total / 10));
    } catch (error) {
      // Error fetching donors - handled silently
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => setSearch(e.target.value);
  const handleDistrictChange = (e) => {
    setDistrict(e.target.value);
    setUpazila(""); // reset upazila when district changes
  };
  const handleUpazilaChange = (e) => {
    setUpazila(e.target.value);
  };
  const handlePageChange = (newPage) => setPage(newPage);

  const getDistrictById = (id) => {
    const district = districts.find((district) => district.id === id);
    return district ? district.bn_name : null;
  };

  const getUpazilaById = (id) => {
    const upazila = upazilas.find((upazila) => upazila.id === id);
    return upazila ? upazila.bn_name : null;
  };


  const handleEdit = (id) => {
    // Navigate to add-blood-donor page with ID for editing
    window.location.href = `/dashboard/add-blood-donor/${id}`;
  };

  const handleDelete = async (id) => {
    setIsLoading(true);
    try {
      await api.delete(`/blood-donor/${id}`);
      toast.success("Blood donor successfully deleted");
      fetchDonors();
    } catch (error) {
      // Error deleting donor - handled silently
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">Manage Blood Donors</h1>
      <div className="mb-4 flex flex-col md:flex-row gap-4">
        <input
          type="text"
          placeholder="Search"
          value={search}
          onChange={handleSearch}
          className="input input-bordered flex-grow"
        />
        <select
          onChange={(e) => setBloodGroup(e.target.value)}
          className="select select-bordered flex-grow"
        >
          <option value="">Select Blood Group</option>
          {bloodGroups.map((group) => (
            <option key={group} value={group}>
              {group}
            </option>
          ))}
        </select>
        <select
          value={district}
          onChange={handleDistrictChange}
          className="select select-bordered flex-grow"
        >
          <option value="">Select District</option>
          {districts.map((dist) => (
            <option key={dist.id} value={dist.id}>
              {dist.bn_name}
            </option>
          ))}
        </select>
        <select
          value={upazila}
          onChange={handleUpazilaChange}
          className="select select-bordered flex-grow"
        >
          <option value="">Select Upazila</option>
          {upazilas
            .filter((upa) => upa.district_id === district)
            .map((upa) => (
              <option key={upa.id} value={upa.id}>
                {upa.bn_name}
              </option>
            ))}
        </select>
      </div>
      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <div>
          <table className="table-auto w-full">
            <thead>
              <tr>
                <th>Profile Image</th>
                <th>Name</th>
                <th>Blood Group</th>
                <th>Mobile</th>
                <th>District</th>
                <th>Upazila</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {donors.map((donor) => (
                <tr className="pb-5" key={donor._id}>
                  <td>
                    <img
                      src={
                        `${baseUrl}${donor.profileImage}` || donorPlaceholder
                      }
                      onError={(e) => (e.target.src = donorPlaceholder)}
                      alt={donor.name}
                      className="w-16 h-16 object-cover rounded-full"
                    />
                  </td>
                  <td>{donor.name}</td>
                  <td className="text-red-500 font-bold">{donor.bloodGroup}</td>
                  <td>{donor.mobile}</td>
                  <td>{donor.district}</td>
                  <td>{donor.upazila}</td>
                  <td className="flex gap-2">
                    <button
                      onClick={() => handleEdit(donor._id)}
                      className="btn btn-sm btn-primary"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(donor._id)}
                      className="btn btn-sm btn-danger"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Improved Pagination */}
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{(page - 1) * 10 + 1}</span>{" "}
              to{" "}
              <span className="font-medium">
                {Math.min(page * 10, (page - 1) * 10 + donors.length)}
              </span>{" "}
              of <span className="font-medium">{totalPages * 10}</span> results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={page === 1}
                className="btn btn-outline btn-sm"
              >
                First
              </button>
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="btn btn-outline btn-sm"
              >
                Previous
              </button>
              {[...Array(Math.min(5, totalPages)).keys()].map((num) => {
                // Show limited page numbers
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = num + 1;
                } else if (page <= 3) {
                  pageNum = num + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + num;
                } else {
                  pageNum = page - 2 + num;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${
                      page === pageNum ? "btn-active" : "btn-outline"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {totalPages > 5 && page < totalPages - 2 && (
                <>
                  <span className="px-3 py-1">...</span>
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className={`btn btn-sm ${
                      page === totalPages ? "btn-active" : "btn-outline"
                    }`}
                  >
                    {totalPages}
                  </button>
                </>
              )}
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="btn btn-outline btn-sm"
              >
                Next
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={page === totalPages}
                className="btn btn-outline btn-sm"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageBloodDonor;
